import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  onAuthStateChanged,
  updateEmail,
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import getSubscriptionStatus from "../backend/getsub"
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  getDocs,
  deleteField,
} from "firebase/firestore";

import {
  ref,
  listAll,
  deleteObject
} from "firebase/storage";

import { auth, db, storage } from "../firebase";
import "../css/dash.css";

export default function Settings() {
  const [userData, setUserData] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const nav = useNavigate();

  // Load user + Firestore data
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        nav("/");
        return;
      }
      checkSub()
      const refUser = doc(db, "users", user.uid);
      const snap = await getDoc(refUser);

      if (snap.exists()) {
        const data = snap.data();
        setUserData({
          uid: user.uid,
          email: user.email,
          name: data.name || "",
          businessName: data.bname || data.businessName || "",
          menus: data.menus || [],
          stripeLink: data.stripeLink || "",
        });
      }
    });

    return () => unsub();
  }, [nav]);
  const checkSub = async() => {
    const subStatus = await getSubscriptionStatus()
    if(!subStatus.active){
        nav("/checkout")
    }
  }
  // 🔥 Delete all files in a storage folder recursively
  const deleteFolderRecursive = async (folderPath) => {
    const folderRef = ref(storage, folderPath);
    const list = await listAll(folderRef);

    for (const file of list.items) {
      await deleteObject(file);
    }

    for (const subfolder of list.prefixes) {
      await deleteFolderRecursive(subfolder.fullPath);
    }
  };

  // 🔥 Delete whole subcollection
  const deleteSubcollection = async (parentRef, subPath) => {
    const subRef = collection(parentRef, subPath);
    const snap = await getDocs(subRef);

    for (const s of snap.docs) {
      await deleteDoc(s.ref);
    }
  };

  // 🔥 DELETE ACCOUNT (fully safe, no Stripe backend needed)
  const deleteAccount = async () => {
    if (!userData) return;

    if (!window.confirm("Delete your account permanently?")) return;
    if (!window.confirm("This deletes ALL menus and billing data. Continue?")) return;

    const password = prompt("Re-enter your password:");
    if (!password) return alert("Password required.");

    try {
      setDeleting(true);

      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, password);

      // 1️⃣ Reauth
      await reauthenticateWithCredential(user, credential);

      const userRef = doc(db, "users", user.uid);
      const snap = await getDoc(userRef);
      const data = snap.data() || {};
      const menus = data.menus || [];

      // 2️⃣ Delete Stripe Firestore data (safe)
      await deleteSubcollection(userRef, "subscriptions");
      await deleteSubcollection(userRef, "checkout_sessions");
      await deleteSubcollection(userRef, "payments");

      await updateDoc(userRef, {
        stripeCustomerId: deleteField()
      });

      // 3️⃣ Delete all menu data
      for (const menuId of menus) {
        // Remove menu images
        await deleteFolderRecursive(`menus/${menuId}`);

        // Delete categories & items
        const catSnap = await getDocs(collection(db, "menus", menuId, "categories"));

        for (const cat of catSnap.docs) {
          const itemsSnap = await getDocs(
            collection(db, "menus", menuId, "categories", cat.id, "items")
          );

          for (const item of itemsSnap.docs) {
            console.log("Deleting: ", item.ref);
            await deleteDoc(item.ref);
          }
        console.log("Deleting: ", cat.ref);
          await deleteDoc(cat.ref);
        }

        // Delete the menu document
        console.log("Deleting ,menus");
        await deleteDoc(doc(db, "menus", menuId));
      }

      // 4️⃣ Delete user folder (if exists)
      console.log("Deleting folders");
      await deleteFolderRecursive(`users/${user.uid}`);
        console.log("Deleting:" + userRef)
      // 5️⃣ Delete Firestore user doc
      await deleteDoc(userRef);

      // 6️⃣ Delete Firebase Auth account
      console.log("Deleting: user auth");
      await deleteUser(user);

      alert("Your account has been deleted.");
      window.location.href = "/";
    } catch (e) {
      console.error(e);
      alert("Error deleting account: " + e.message);
    } finally {
      setDeleting(false);
    }
  };

  // Save settings
  const handleSave = async () => {
    if (!userData) return;
    setSaving(true);

    try {
      const { uid, name, businessName, email } = userData;

      await updateDoc(doc(db, "users", uid), {
        name,
        businessName,
      });

      if (email !== auth.currentUser.email) {
        await updateEmail(auth.currentUser, email);
      }

      alert("Saved!");
    } catch (e) {
      alert("Error saving settings: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!userData) {
    return (
      <div className="loadingContainer">
        <h2>Loading settings...</h2>
      </div>
    );
  }

  return (
    <section className="dashboard">
      {/* Sidebar */}
      <aside className={`sidebar ${menuOpen ? "open" : ""}`}>
        <h3 className="closeSideBar" onClick={() => setMenuOpen(false)}>X</h3>
        <h2 className="logo">MenuLinker</h2>
        <nav>
          <ul>
            <li onClick={() => nav("/dash")}>Dashboard</li>
            <li onClick={() => nav("/analytics")}>Analytics</li>
            <li className="active">Settings</li>
            <li><a href="mailto:tglewis247@gmail.com">Contact</a></li>
          </ul>
        </nav>
        <button
          className="logoutBtn"
          onClick={async () => {
            await auth.signOut();
            nav("/");
          }}
        >
          Log out
        </button>
      </aside>

      {/* Main content */}
      <main className="mainContent">
        <header className="dashHeader">
          <button className="menuToggle" onClick={() => setMenuOpen(!menuOpen)}>☰</button>
          <div>
            <h1>Settings ⚙️</h1>
            <p>Manage your account and preferences</p>
          </div>
        </header>

        <section className="cardsGrid">
          {/* Account Info */}
          <div className="card">
            <h3>Account</h3>

            <input
              className="settingsInput"
              value={userData.name}
              placeholder="Your Name"
              onChange={(e) => setUserData({ ...userData, name: e.target.value })}
            />

            <input
              className="settingsInput"
              value={userData.email}
              placeholder="Your Email"
              onChange={(e) => setUserData({ ...userData, email: e.target.value })}
            />

            <input
              className="settingsInput"
              value={userData.businessName}
              placeholder="Business Name"
              onChange={(e) => setUserData({ ...userData, businessName: e.target.value })}
            />

            <button
              className="menu-btn edit"
              disabled={saving}
              onClick={handleSave}
              style={{ padding: 10 }}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>

            {userData.stripeLink && (
              <button
                className="menu-btn edit"
                onClick={() => window.location.replace("https://billing.stripe.com/p/login/28E28rfMY4s40988wZe7m00")}
                style={{ padding: 10 }}
              >
                Billing Portal
              </button>
            )}
          </div>

          {/* Danger Zone */}
          <div className="card" style={{ border: "2px solid #f44336" }}>
            <h3 style={{ color: "#f44336" }}>Danger Zone</h3>
            <p>Delete your account and all data permanently.</p>

            <button
              className="menu-btn delete"
              style={{ background: "#f44336", padding: 10 }}
              onClick={deleteAccount}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete Account"}
            </button>
          </div>
        </section>
      </main>
    </section>
  );
}
